import type { Database } from '@/types_db';
import { GEAR } from '@/utils/constants';

type Enums = Database['public']['Enums'];

export type PolicyCategory = Enums['tone_policy_category'];
export type Severity = Enums['tone_policy_severity'];
export type Confidence = Enums['tone_policy_confidence'];
export type VerdictSource = Enums['tone_policy_source'];
export type VerdictStatus = Enums['tone_policy_status'];
export type PolicyMode = Enums['tone_policy_mode'];
export type NotEnforcedReason = Enums['tone_policy_not_enforced'];
export type SkipReason = Enums['tone_policy_skip_reason'];

export const POLICY_CATEGORIES = [
  'piracy',
  'redistribution',
  'harmful-content',
  'image-quality',
  'mixed-gear',
] as const satisfies readonly PolicyCategory[];

export const CONFIDENCES = ['high', 'medium', 'low'] as const satisfies readonly Confidence[];

/** The gear types a creator can declare; a wrong-pack-type finding names one. */
export const PACK_GEARS: readonly GEAR[] = Object.values(GEAR);

export const isPackGear = (value: string | null | undefined): value is GEAR =>
  (PACK_GEARS as readonly string[]).includes(value ?? '');

/** Sources whose verdicts a newer pending one replaces; backfill never strikes and reports carry context. */
export const SUPERSEDABLE_SOURCES = ['publish', 'edit'] as const satisfies readonly VerdictSource[];

/** The rule a violation breaks, by category. "other" is for a violation no rule covers. */
export const POLICY_RULES = {
  piracy: [
    'current-plugin',
    'plugin-in-chain',
    'unidentified-plugin',
    'unclear-gear',
    'current-digital-gear',
    'current-cab-sim',
    'commercial-ir',
    'commercial-ir-in-chain',
    'all-plugin-chain',
    'not-real-gear',
  ],
  redistribution: ['stated-source'],
  'harmful-content': ['harmful-content'],
  'image-quality': ['not-a-photo', 'wrong-gear', 'needs-diagram', 'needs-no-image'],
  'mixed-gear': ['several-gear', 'wrong-pack-type', 'amp-through-cabs', 'genre-or-artist-pack'],
} as const satisfies Record<PolicyCategory, readonly string[]>;

export const RULES = [
  ...POLICY_RULES.piracy,
  ...POLICY_RULES.redistribution,
  ...POLICY_RULES['harmful-content'],
  ...POLICY_RULES['image-quality'],
  ...POLICY_RULES['mixed-gear'],
  'other',
] as const;

export type PolicyRule = (typeof RULES)[number];

/** No longer reported or acted on; kept so stored verdicts still type and render. */
export const RETIRED_RULES: readonly PolicyRule[] = ['amp-through-cabs'];

export const REPORTABLE_RULES = RULES.filter((rule) => !RETIRED_RULES.includes(rule));

/** One violation as the classifier reports it, before severity is attached. */
export interface Finding {
  category: PolicyCategory;
  rule: PolicyRule;
  confidence: Confidence;
  evidence: string;
  /** wrong-pack-type only: the gear type the mismatched captures belong in. */
  gear_type: GEAR | null;
}

export interface Violation extends Finding {
  severity: Severity;
}
